import React from 'react';
import { Box, Link as ChakraLink } from '@chakra-ui/react';
import { assetUrl } from '../../utils/url';

export interface Banner {
  id: number | string;
  name: string;
  image?: string;
  image_url?: string;
  url?: string;
  click_url?: string;
  placement?: string;
  width?: number;
  height?: number;
  is_active?: boolean;
}

interface BannerDisplayProps {
  banners: Banner[];
  placement: 'homepage_top' | 'homepage_middle' | 'homepage_footer' | 'article_inline' | 'homepage_under_table';
  containerStyle?: React.CSSProperties;
}

const BannerDisplay: React.FC<BannerDisplayProps> = ({ banners, placement, containerStyle }) => {
  // Filter active banners for this placement
  const activeBanners = (banners || []).filter(
    b => b.placement === placement && (b.is_active !== false)
  );

  if (activeBanners.length === 0) {
    return null;
  }

  const getContainerClass = () => {
    switch (placement) {
      case 'homepage_top':
        return 'banner-top';
      case 'homepage_middle':
        return 'banner-middle';
      case 'homepage_footer':
        return 'banner-footer';
      case 'article_inline':
        return 'banner-article';
      case 'homepage_under_table':
        return 'banner-under-table';
      default:
        return 'banner';
    }
  };

  const getDefaultContainerStyle = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      margin: '24px 0',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '16px',
      flexWrap: 'wrap',
    };

    switch (placement) {
      case 'homepage_top':
        return {
          ...base,
          width: '100vw',
          position: 'relative',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          padding: '16px',
          borderTop: '1px solid rgba(0, 0, 0, 0.05)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
        };
      case 'homepage_footer':
        return {
          ...base,
          width: '100vw',
          position: 'relative',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          padding: '24px 16px',
          borderTop: '1px solid rgba(0, 0, 0, 0.05)',
        };
      case 'homepage_under_table':
        return {
          ...base,
          margin: '12px 0 0',
          justifyContent: 'center',
        };
      default:
        return base;
    }
  };

  const finalContainerStyle = { ...getDefaultContainerStyle(), ...containerStyle };

  return (
    <Box
      as="section"
      className={getContainerClass()}
      sx={finalContainerStyle}
    >
      {activeBanners.map((banner) => (
        <ChakraLink
          key={banner.id}
          href={banner.url || banner.click_url || '#'}
          isExternal={!!(banner.url || banner.click_url)}
          target={(banner.url || banner.click_url) ? '_blank' : undefined}
          rel={(banner.url || banner.click_url) ? 'noopener noreferrer' : undefined}
          display="inline-block"
          _hover={{ opacity: 0.9, transform: 'translateY(-2px)' }}
          transition="all 0.2s"
        >
          <img
            src={assetUrl(banner.image || banner.image_url || '') || banner.image || banner.image_url || ''}
            alt={banner.name}
            style={{
              maxWidth: '100%',
              width: banner.width ? `${banner.width}px` : 'auto',
              height: banner.height ? `${banner.height}px` : 'auto',
              objectFit: 'contain',
              borderRadius: '4px',
              boxShadow: 'none',
            }}
            loading="lazy"
          />
        </ChakraLink>
      ))}
    </Box>
  );
};

export default BannerDisplay;
